<template>
  <EntityList
    title="Emails"
    :items="emails"
    :loading="loading"
    :filters="emailStore.filters"
    :sortField="emailStore.sort.field ?? undefined"
    :sortOrder="emailStore.sort.order ?? undefined"
    :lastLoadedAt="emailStore.lastLoadedAt"
    :onReload="reload"
    :globalFilterFields="['to', 'from', 'message.subject', 'sentAt', 'delivery.state']"
    :columns="columns"
    @view="viewEmail"
    @update:filters="emailStore.setFilters"
    @update:sort="emailStore.setSort"
    @reset:filters="resetFilters()"
  >
    <template #actions>
      <Button label="Compose Email" icon="pi pi-plus" @click="router.push('/crm/emailer')" />
    </template>

    <template #rowActions="{ data }">
      <div class="flex gap-2 justify-content-center">
        <Button
          icon="pi pi-eye"
          class="p-button-rounded p-button-text"
          @click="viewEmail(data)"
          aria-label="View Email"
        />
      </div>
    </template>

    <template #deliveryState="{ data }">
      <Tag
        :value="EmailModel.getDeliveryStatusSummary(data).status"
        :severity="EmailModel.getDeliveryStatusSummary(data).statusClass"
      />
    </template>

    <template #recipient="{ data }">
      <span>{{ EmailModel.formatRecipient(data.to) }}</span>
    </template>

    <template #sentDate="{ data }">
      <span>{{ EmailModel.formatDate(data.sentAt) }}</span>
    </template>

    <template #subject="{ data }">
      <span>{{ data.message?.subject }}</span>
    </template>
  </EntityList>

  <!-- Email Detail Dialog -->
  <Dialog
    v-model:visible="showEmailDetailDialog"
    :style="{ width: '80%', maxWidth: '800px' }"
    :modal="true"
    :closable="true"
    header="Email Details"
  >
    <div v-if="selectedEmail">
      <div class="email-details">
        <div class="email-header">
          <div class="field">
            <label>Subject</label>
            <div>{{ selectedEmail.message?.subject }}</div>
          </div>
          <div class="field">
            <label>From</label>
            <div>{{ selectedEmail.from || 'N/A' }}</div>
          </div>
          <div class="field">
            <label>To</label>
            <div>{{ EmailModel.formatRecipient(selectedEmail.to) }}</div>
          </div>
          <div class="field">
            <label>Sent</label>
            <div>{{ EmailModel.formatDate(selectedEmail.sentAt) }}</div>
          </div>
          <div class="field">
            <label>Status</label>
            <Tag
              :value="EmailModel.getDeliveryStatusSummary(selectedEmail).status"
              :severity="EmailModel.getDeliveryStatusSummary(selectedEmail).statusClass"
            />
          </div>
        </div>
        <div class="email-body">
          <div v-if="selectedEmail.message?.html" v-html="selectedEmail.message.html"></div>
          <div v-else-if="selectedEmail.message?.text">{{ selectedEmail.message.text }}</div>
          <div v-else class="no-content">No content available</div>
        </div>
        <div v-if="selectedEmail.delivery?.info?.messageId" class="email-footer">
          <small>Message ID: {{ selectedEmail.delivery.info.messageId }}</small>
        </div>
      </div>
    </div>
    <template #footer>
      <Button
        v-if="
          selectedEmail && (EmailModel.hasDeliveryFailed(selectedEmail) || !selectedEmail.delivery)
        "
        label="Retry"
        icon="pi pi-refresh"
        @click="retryEmail"
        class="p-button-warning"
      />
      <Button
        label="Close"
        icon="pi pi-times"
        @click="showEmailDetailDialog = false"
        class="p-button-text"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useEmailStore, type Email } from '@/stores/emailStore'
import { EmailModel } from '@/models/EmailModel'
import { useEmailService } from '@/services/emailService'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'

import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import EntityList from '@/components/base/EntityList.vue'

const emailStore = useEmailStore()
const emailService = useEmailService()
const toast = useToast()
const router = useRouter()

const loading = ref(false)
const showEmailDetailDialog = ref(false)
const selectedEmail = ref<Email | null>(null)
const emails = ref<Email[]>([])

const resetFilters = () => {
  emailStore.resetFilters()
  emailStore.resetSort()
}

// Column definitions
const columns = [
  {
    field: 'delivery.state',
    header: 'Status',
    sortable: true,
    filterable: true,
    slot: 'deliveryState',
    filterOptions: [
      { label: 'Pending', value: 'PENDING' },
      { label: 'Processing', value: 'PROCESSING' },
      { label: 'Success', value: 'SUCCESS' },
      { label: 'Error', value: 'ERROR' },
      { label: 'Retry', value: 'RETRY' },
    ],
  },
  {
    field: 'to',
    header: 'Recipient',
    sortable: true,
    filterable: true,
    slot: 'recipient',
  },
  {
    field: 'message.subject',
    header: 'Subject',
    sortable: true,
    filterable: true,
    slot: 'subject',
  },
  {
    field: 'sentAt',
    header: 'Sent Date',
    sortable: true,
    filterable: true,
    slot: 'sentDate',
    dataType: 'date',
  },
]

function viewEmail(email: Email) {
  console.log('viewEmail', email)
  selectedEmail.value = email
  if (email.id) {
    emailStore.setCurrentItem(email.id)
  }
  showEmailDetailDialog.value = true
}

async function retryEmail() {
  if (selectedEmail.value?.id) {
    const success = await emailService.retryEmail(selectedEmail.value.id)
    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Email scheduled for retry',
        life: 3000,
      })
      await reload()
      showEmailDetailDialog.value = false
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to schedule email for retry',
        life: 5000,
      })
    }
  }
}

const reload = async () => {
  loading.value = true
  emails.value = await emailService.getRecentEmails()
  loading.value = false
}

onMounted(async () => {
  await reload()
})
</script>

<style scoped>
.email-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.email-header {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;
}

.email-header .field {
  margin-bottom: 0.5rem;
}

.email-header label {
  font-weight: bold;
  display: block;
  margin-bottom: 0.25rem;
  color: #666;
}

.email-body {
  padding: 1rem 0;
  min-height: 200px;
}

.email-footer {
  border-top: 1px solid #eee;
  padding-top: 0.5rem;
  color: #666;
}

.no-content {
  color: #999;
  font-style: italic;
}
</style>
